import { useCallback, useEffect, useState } from 'react';
import {
  useAllCapabilitiesForUserQuery,
  useSelfQuery,
} from 'hooks/useRequest.generated';
import { ICapabilitiesForUser, ISelf, Maybe } from 'types/types.generated';
import { IAuthUser } from 'types/authUser';
import { buildAuthUser } from '../utils/authUserUtils';

interface IAuthUserServiceProps {
  isAuthenticated: boolean;
  loadUserOnSuccess: () => void;
  loadUserOnFailure: () => void;
}

/**
 * Hook to get the populate the current Authenticated user information based on various sources.
 * Don't use directly but instead use the useAuthUser hook with the AuthUser Context.
 */
export const useAuthUserFetcher = ({
  isAuthenticated,
  loadUserOnSuccess,
  loadUserOnFailure,
}: IAuthUserServiceProps) => {
  const [authUser, setAuthUser] = useState<Maybe<IAuthUser>>(null);
  const [loadUser, setLoadUser] = useState(false);

  // Gate on isAuthenticated so a session-expiry race doesn't fire /self
  // or /capabilities while the auth session is being torn down.
  const enabled = loadUser && isAuthenticated;

  const {
    isSuccess: selfIsSuccess,
    isError: selfIsError,
    data: selfRes,
  } = useSelfQuery({}, { enabled });

  const {
    isSuccess: capabilitiesIsSuccess,
    isError: capabilitiesIsError,
    data: capabilitiesRes,
  } = useAllCapabilitiesForUserQuery({}, { enabled });

  const self: Maybe<ISelf> = selfRes?.self || null;

  const capabilities: Maybe<ICapabilitiesForUser> =
    capabilitiesRes?.allCapabilitiesForUser || null;

  const isError = selfIsError || capabilitiesIsError;

  useEffect(() => {
    if (selfIsSuccess && self && capabilitiesIsSuccess && capabilities) {
      const curAuthUser = buildAuthUser(self, capabilities);
      setAuthUser(curAuthUser);
      loadUserOnSuccess();
    }

    if (isError) {
      loadUserOnFailure();
    }
  }, [
    capabilities,
    capabilitiesIsSuccess,
    isError,
    loadUserOnFailure,
    loadUserOnSuccess,
    self,
    selfIsSuccess,
  ]);

  /**
   * Once the auth session is authenticated via PI, populate the current auth user bu calling various sources.
   */
  const loadAuthUser = useCallback((): void => {
    setLoadUser(true);
  }, []);

  /**
   * Clear the auth user on session sign-out so it can start all over again.
   */
  const clearAuthUser = useCallback((): void => {
    setLoadUser(false);
  }, []);

  return {
    authUser,
    loadAuthUser,
    clearAuthUser,
  };
};
